"use client"

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowLeft01Icon,
  Search01Icon,
  Building02Icon,
  UserIcon,
  Call02Icon,
  Mail01Icon,
  Calendar01Icon,
  Money03Icon,
} from '@hugeicons/core-free-icons'
import { get } from '../../utils/service'

interface ClientResult {
  id: string
  name: string
  contact_person: string | null
  phone: string | null
  status: string
}

interface LoanResult {
  id: string
  client_id: string
  principal: string
  currency: string
  effective_status: string
  due_date: string
  client?: { name: string }
}

interface UserResult {
  id: string
  first_name: string
  last_name: string
  email: string
  role: string
}

interface SearchResponse {
  success: boolean
  message: string
  data: {
    clients: ClientResult[]
    loans: LoanResult[]
    users: UserResult[]
  }
}

const formatLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())

const formatCurrency = (amount: string | number, currency: string = 'UGX') => {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(n)) return `${currency} 0`
  return `${currency} ${n.toLocaleString()}`
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'redeemed':
    case 'active':
      return 'text-green-600'
    case 'due_soon':
      return 'text-amber-600'
    case 'expiring_today':
      return 'text-orange-600'
    case 'expired':
      return 'text-red-600'
    default:
      return 'text-gray-600'
  }
}

const SearchResultsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') || ''

  const [results, setResults] = useState<SearchResponse['data'] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (query) {
      fetchSearchResults()
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const fetchSearchResults = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await get<SearchResponse>(`/search?q=${encodeURIComponent(query)}`)

      if (response.success) {
        setResults(response.data)
      } else {
        setError('No results found')
      }
    } catch (err) {
      setError('Failed to load search results')
      console.error('Search error:', err)
    } finally {
      setLoading(false)
    }
  }

  const totalResults = results ? results.clients.length + results.loans.length + results.users.length : 0

  const SkeletonCard = () => (
    <div className="animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-32"></div>
    </div>
  )

  const renderClient = (client: ClientResult) => (
    <motion.div
      key={client.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="pb-4 mb-4 border-b border-gray-100 last:border-0 last:mb-0 cursor-pointer"
      onClick={() => navigate('/clients')}
    >
      <div className="flex items-start gap-3">
        <HugeiconsIcon icon={Building02Icon} size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-900 mb-1">{client.name}</h3>
          {client.contact_person && <p className="text-xs text-gray-600 mb-2">{client.contact_person}</p>}
          <div className="flex items-center gap-4 mb-1 flex-wrap">
            <span className={`text-xs font-medium ${getStatusColor(client.status)}`}>{formatLabel(client.status)}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-600 flex-wrap">
            {client.phone && (
              <div className="flex items-center gap-1">
                <HugeiconsIcon icon={Call02Icon} size={12} />
                {client.phone}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )

  const renderLoan = (loan: LoanResult) => (
    <motion.div
      key={loan.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="pb-4 mb-4 border-b border-gray-100 last:border-0 last:mb-0 cursor-pointer"
      onClick={() => navigate(`/loans?highlight=${loan.id}`)}
    >
      <div className="flex items-start gap-3">
        <HugeiconsIcon icon={Money03Icon} size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-900 mb-1">{loan.client?.name || 'Loan'}</h3>
          <p className="text-xs text-gray-600 mb-2">{formatCurrency(loan.principal, loan.currency)}</p>
          <div className="flex items-center gap-4 mb-1 flex-wrap">
            <span className={`text-xs font-medium ${getStatusColor(loan.effective_status)}`}>{loan.effective_status === 'redeemed' ? 'Paid' : formatLabel(loan.effective_status)}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <HugeiconsIcon icon={Calendar01Icon} size={12} />
              Due {new Date(loan.due_date).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )

  const renderUser = (user: UserResult) => (
    <motion.div
      key={user.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="pb-4 mb-4 border-b border-gray-100 last:border-0 last:mb-0 cursor-pointer"
      onClick={() => navigate('/users')}
    >
      <div className="flex items-start gap-3">
        <HugeiconsIcon icon={UserIcon} size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-900 mb-1">{user.first_name} {user.last_name}</h3>
          <div className="flex items-center gap-4 mb-1 flex-wrap text-xs text-gray-600">
            <span>{formatLabel(user.role)}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <HugeiconsIcon icon={Mail01Icon} size={12} />
            {user.email}
          </div>
        </div>
      </div>
    </motion.div>
  )

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors text-xs"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={12} />
            Back
          </button>

          <div className="flex items-center gap-3 mb-2">
            <HugeiconsIcon icon={Search01Icon} size={16} className="text-gray-400" />
            <h1 className="text-sm font-semibold text-gray-900">Search Results</h1>
          </div>

          <p className="text-xs text-gray-600">
            {loading ? 'Searching...' : `${totalResults} result${totalResults !== 1 ? 's' : ''} for`}
            {!loading && <span className="font-medium text-gray-900"> "{query}"</span>}
          </p>
        </div>

        {/* Results */}
        <div className="space-y-0">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-600">{error}</p>
            </div>
          ) : !results || totalResults === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-600">
                No results found for "<span className="font-medium">{query}</span>"
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {results.clients.length > 0 && (
                <section>
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Clients</h2>
                  {results.clients.map(renderClient)}
                </section>
              )}
              {results.loans.length > 0 && (
                <section>
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Loans</h2>
                  {results.loans.map(renderLoan)}
                </section>
              )}
              {results.users.length > 0 && (
                <section>
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Users</h2>
                  {results.users.map(renderUser)}
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchResultsPage
